import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import {
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

const STATUS_CONFIG = {
  pending:   { label: 'En attente',  Icon: ClockIcon,        color: 'text-yellow-700', bg: 'bg-yellow-50',  border: 'border-yellow-200' },
  approved:  { label: 'Approuvé',    Icon: CheckCircleIcon,  color: 'text-green-700',  bg: 'bg-green-50',   border: 'border-green-200' },
  declined:  { label: 'Refusé',      Icon: XCircleIcon,      color: 'text-red-700',    bg: 'bg-red-50',     border: 'border-red-200' },
  cancelled: { label: 'Annulé',      Icon: XCircleIcon,      color: 'text-gray-600',   bg: 'bg-gray-50',    border: 'border-gray-200' },
  refunded:  { label: 'Remboursé',   Icon: ArrowPathIcon,    color: 'text-blue-700',   bg: 'bg-blue-50',    border: 'border-blue-200' }
};

const PLAN_BADGE = {
  gratuit:   { label: 'Sans abonnement',  cls: 'bg-gray-100 text-gray-700' },
  basique:   { label: 'Basique',          cls: 'bg-sky-100 text-sky-700' },
  pro:       { label: 'Pro',              cls: 'bg-blue-100 text-blue-700' },
  promax:    { label: 'Pro Max',          cls: 'bg-primary-100 text-primary-700' },
  'boost-ia':{ label: "Boostez avec l'IA", cls: 'bg-purple-100 text-purple-700' }
};

const Payment = () => {
  const { data: currentSub, isLoading: subLoading } = useQuery(
    'current-subscription',
    () => axios.get('/api/subscriptions/current').then(r => r.data)
  );

  const { data: historyData, isLoading: histLoading } = useQuery(
    'payment-history',
    () => axios.get('/api/payments/history').then(r => r.data)
  );

  const payments = historyData?.payments || [];
  const badge = PLAN_BADGE[currentSub?.planId] || PLAN_BADGE['gratuit'];

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-text mb-8 flex items-center">
          <CreditCardIcon className="h-8 w-8 mr-3 text-primary-600" />
          Facturation & Paiements
        </h1>

        {/* Abonnement actuel */}
        <div className="card p-6 mb-8">
          <h2 className="text-lg font-semibold text-text mb-4">Abonnement actuel</h2>
          {subLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-6 bg-gray-100 rounded w-1/3"></div>
              <div className="h-4 bg-gray-100 rounded w-1/2"></div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${badge.cls}`}>
                    {badge.label}
                  </span>
                  {currentSub?.status === 'active' && (
                    <span className="flex items-center text-green-600 text-sm font-medium">
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      Actif
                    </span>
                  )}
                </div>
                {currentSub?.renewalDate ? (
                  <p className="text-sm text-gray-500 mt-1">
                    Renouvellement le {new Date(currentSub.renewalDate).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 mt-1">{currentSub?.planId === 'gratuit' ? 'Sans abonnement : consultation seulement' : 'Abonnement en cours'}</p>
                )}
              </div>
              <div className="flex gap-3">
                <Link
                  to="/subscriptions"
                  className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm font-medium"
                >
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                  Changer de plan
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Ce qui est inclus */}
        {currentSub && currentSub.planId !== 'gratuit' && (
          <div className="card p-6 mb-8 bg-green-50 border border-green-200">
            <h2 className="text-sm font-semibold text-green-800 mb-2 uppercase tracking-wide">Inclus dans votre plan</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-green-700">
              {currentSub.planId === 'pro' && <>
                <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> Gestion d'entreprise complète</span>
                <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> AgroGestion</span>
                <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> Kit360° (40+ outils)</span>
                <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> Publications illimitées</span>
                <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> Outils & ressources</span>
              </>}
              {currentSub.planId === 'promax' && <>
                <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> Tout le plan Pro</span>
                <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> Business Plans illimités</span>
                <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> 10 collaborateurs</span>
                <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> Produits illimités (Marketplace)</span>
                <span className="flex items-center"><CheckCircleIcon className="h-4 w-4 mr-1" /> Support prioritaire</span>
              </>}
            </div>
          </div>
        )}

        {/* Historique des paiements */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-text mb-4">Historique des paiements</h2>
          {histLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="animate-pulse h-16 bg-gray-100 rounded-lg"></div>
              ))}
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-12">
              <CreditCardIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 mb-1">Aucun paiement enregistré</p>
              <p className="text-sm text-gray-400">Vos factures apparaîtront ici après un paiement</p>
              <Link
                to="/subscriptions"
                className="inline-block mt-4 text-primary-600 hover:underline text-sm font-medium"
              >
                Découvrir les plans disponibles →
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {payments.map(payment => {
                const cfg = STATUS_CONFIG[payment.status] || STATUS_CONFIG.pending;
                const { Icon } = cfg;
                const planBadge = PLAN_BADGE[payment.plan_id] || PLAN_BADGE['gratuit'];
                return (
                  <div
                    key={payment.id}
                    className={`flex items-center justify-between p-4 rounded-lg border ${cfg.bg} ${cfg.border}`}
                  >
                    <div className="flex items-center min-w-0">
                      <Icon className={`h-5 w-5 mr-3 flex-shrink-0 ${cfg.color}`} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-text text-sm">
                            {payment.description || `Abonnement ${payment.plan_id}`}
                          </p>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${planBadge.cls}`}>
                            {planBadge.label}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {new Date(payment.created_at).toLocaleDateString('fr-FR', {
                            year: 'numeric', month: 'long', day: 'numeric'
                          })}
                          {payment.fedapay_transaction_id && (
                            <span className="ml-2 text-gray-400">#{payment.fedapay_transaction_id}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right ml-4 flex-shrink-0">
                      {payment.amount_xof > 0 && (
                        <p className="font-bold text-text text-sm">{payment.amount_xof.toLocaleString('fr-FR')} FCFA</p>
                      )}
                      <p className={`text-xs font-semibold mt-1 ${cfg.color}`}>{cfg.label}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Note sécurité */}
        <p className="text-center text-xs text-gray-400 mt-6">
          Les paiements sont traités de manière sécurisée via FedaPay.
          Aucune donnée de carte bancaire n'est stockée sur nos serveurs.
        </p>
      </div>
    </div>
  );
};

export default Payment;
